"use client";

import { useCallback, useContext, useMemo } from "react";
import { usePathname } from "next/navigation";
import { useStatsigClient as useOriginalStatsigClient } from "@statsig/react-bindings";
import type { StatsigEvent } from "@statsig/js-client";
import { InitialContext, DefaultInitialContext } from "@repo/sdk";
import { deriveSurface } from "../utils/derive-surface";
import { deriveEntrypointId } from "../utils/derive-entrypoint";

/**
 * Enhanced Statsig client hook that auto-injects global metadata into all events.
 *
 * Drop-in replacement for `useStatsigClient` from `@statsig/react-bindings`.
 * Automatically adds to all logged events:
 * - route: Current pathname
 * - surface: Derived surface category (home, list, checkout, etc.)
 * - entrypoint_id: How the user entered the app (triage, dismissible_cta, fab, etc.)
 *
 * @example
 * // Just change the import:
 * // from: import { useStatsigClient } from "@statsig/react-bindings"
 * // to:   import { useStatsigClient } from "@repo/statsig"
 *
 * const { logEvent } = useStatsigClient();
 * logEvent({ eventName: "cta_clicked", metadata: { action: "start" } });
 * // route, surface, entrypoint_id auto-injected
 */
export function useStatsigClient() {
  const original = useOriginalStatsigClient();
  const pathname = usePathname() ?? "/";

  // Use useContext directly to avoid Suspense throwing behavior
  const contextState = useContext(InitialContext);
  const initialContext: DefaultInitialContext | null = contextState?.data ?? null;

  const globalMetadata = useMemo(
    () => ({
      route: pathname,
      surface: deriveSurface(pathname),
      entrypoint_id: deriveEntrypointId(
        initialContext?.startingRoute ?? pathname
      ),
    }),
    [pathname, initialContext?.startingRoute]
  );

  const logEvent = useCallback(
    (event: StatsigEvent) => {
      original.logEvent({
        ...event,
        metadata: {
          ...globalMetadata,
          ...event.metadata,
        },
      });
    },
    [original, globalMetadata]
  );

  // Return same shape as original, but with wrapped logEvent
  return {
    ...original,
    logEvent,
  };
}

export default useStatsigClient;
